import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

/**
 * <collection-filter-chips>
 *
 * Progressive enhancement for the "Collection filter chips" custom section
 * (sections/collection-filter-chips_custom.liquid). The element has two modes:
 *
 *  - mode="brand"  — chips are canonical links to {brand}-{style}-rackets combo
 *                    collections. We do NOT intercept the click (real navigation to a
 *                    real, indexable page) but we PREFETCH the target on hover/touch so
 *                    the click feels instant (instant.page-style).
 *
 *  - mode="filter" — chips are native facet links. We hand the URL to the theme's
 *                    <facets-form-component> so the grid filters in place (no reload),
 *                    and re-render this bar on ThemeEvents.FilterUpdate to keep active
 *                    states in sync. Falls back to plain navigation if that's unavailable.
 *
 * Either way the chips are real <a> elements, so everything still works with JS off.
 */

const prefetched = new Set();

/** Warm the browser cache for a full-page navigation. */
function prefetch(href) {
  if (!href || prefetched.has(href) || href.charAt(0) === '#') return;
  // Only same-origin document navigations.
  try {
    const url = new URL(href, window.location.href);
    if (url.origin !== window.location.origin) return;
  } catch (_) {
    return;
  }
  prefetched.add(href);
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.as = 'document';
  link.href = href;
  document.head.appendChild(link);
}

class CollectionFilterChips extends HTMLElement {
  connectedCallback() {
    this.mode = this.getAttribute('mode') || 'filter';

    if (this.mode === 'filter') {
      this.addEventListener('click', this.#onFilterClick);
    }
    if (this.mode !== 'brand') document.addEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);

    // Prefetch chip targets on intent (mainly for canonical brand links, but harmless for
    // filter links too). pointerover / focusin / touchstart all bubble to this element.
    this.addEventListener('pointerover', this.#onIntent);
    this.addEventListener('focusin', this.#onIntent);
    this.addEventListener('touchstart', this.#onIntent, { passive: true });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onFilterClick);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);
    this.removeEventListener('pointerover', this.#onIntent);
    this.removeEventListener('focusin', this.#onIntent);
    this.removeEventListener('touchstart', this.#onIntent);
  }

  get sectionId() {
    return this.getAttribute('section-id');
  }

  /** @param {Event} event */
  #onIntent = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const chip = target.closest && target.closest('a.cfc-chip');
    // The premium controller warms reusable content instead of a second document prefetch.
    if (document.documentElement.hasAttribute('data-collection-navigation')) return;
    if (chip && this.contains(chip)) prefetch(chip.getAttribute('href'));
  };

  /**
   * @returns {(HTMLElement & { updateFiltersByURL?: (url: string) => void }) | null}
   */
  #getFacetsForm() {
    return (
      document.querySelector('facets-form-component[section-id]') ||
      document.querySelector('facets-form-component')
    );
  }

  /** @param {MouseEvent} event */
  #onFilterClick = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const target = /** @type {HTMLElement} */ (event.target);
    const chip = target.closest('a.cfc-chip');
    if (!chip || !this.contains(chip)) return;

    const href = chip.getAttribute('href');
    if (!href) return;

    const facetsForm = this.#getFacetsForm();
    if (!facetsForm || typeof facetsForm.updateFiltersByURL !== 'function') {
      // No facets pipeline available — fall back to the anchor's default navigation.
      return;
    }

    event.preventDefault();
    facetsForm.updateFiltersByURL(href);
  };

  // Re-render this section so chip active states / hrefs reflect the new URL (filter mode).
  #onFilterUpdate = () => {
    const id = this.sectionId;
    if (!id || !sectionRenderer) return;
    try {
      sectionRenderer.renderSection(id);
    } catch (error) {
      console.warn('[collection-filter-chips] section re-render failed', error);
    }
  };
}

if (!customElements.get('collection-filter-chips')) {
  customElements.define('collection-filter-chips', CollectionFilterChips);
}

/**
 * <collection-breadcrumb>
 *
 * Companion element for sections/collection-breadcrumb_custom.liquid. Crumbs are
 * canonical links (never intercepted) — this just prefetches them on intent so
 * stepping back up the hierarchy feels as instant as the chips.
 */
class CollectionBreadcrumb extends HTMLElement {
  connectedCallback() {
    this.addEventListener('pointerover', this.#onIntent);
    this.addEventListener('focusin', this.#onIntent);
    this.addEventListener('touchstart', this.#onIntent, { passive: true });
  }

  disconnectedCallback() {
    this.removeEventListener('pointerover', this.#onIntent);
    this.removeEventListener('focusin', this.#onIntent);
    this.removeEventListener('touchstart', this.#onIntent);
  }

  /** @param {Event} event */
  #onIntent = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const link = target.closest && target.closest('a.ccb__link');
    if (link && this.contains(link)) prefetch(link.getAttribute('href'));
  };
}

if (!customElements.get('collection-breadcrumb')) {
  customElements.define('collection-breadcrumb', CollectionBreadcrumb);
}
